import { useMemo } from "react";
import PropTypes from "prop-types";
import Chart from "react-apexcharts";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useICCCColors } from "../theme";

const DAY_MS = 24 * 60 * 60 * 1000;
const TREND_DAYS = 14;

// Alert-count-per-day trend, built from the same active/history alert rows
// already fetched for this device — no new data source (Scope-Out bounds
// charts to the existing alert feed, not live metric gauges).
export default function AlertTrendChart({ alerts }) {
  const ICCC_COLORS = useICCCColors();
  const [controller] = useMaterialUIController();

  const { categories, data, total } = useMemo(() => {
    const now = Date.now();
    // Local midnight, not UTC midnight — a plain now % DAY_MS bucket boundary
    // would be off by the browser's UTC offset (e.g. 5:30h for IST).
    const startOfToday = new Date(now).setHours(0, 0, 0, 0);
    const buckets = Array.from({ length: TREND_DAYS }, (_, i) => startOfToday - (TREND_DAYS - 1 - i) * DAY_MS);
    const counts = buckets.map(
      (dayStart) => alerts.filter((a) => a.raisedAt >= dayStart && a.raisedAt < dayStart + DAY_MS).length
    );
    return {
      categories: buckets.map((d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })),
      data: counts,
      total: counts.reduce((a, b) => a + b, 0),
    };
  }, [alerts]);

  if (!total) {
    return (
      <MDBox sx={{ textAlign: "center", color: ICCC_COLORS.textMuted, py: 4 }}>
        No alert history in the last {TREND_DAYS} days
      </MDBox>
    );
  }

  const options = {
    chart: { type: "bar", toolbar: { show: false }, foreColor: ICCC_COLORS.textSecondary },
    plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
    dataLabels: { enabled: false },
    colors: [ICCC_COLORS.accent],
    xaxis: { categories, labels: { style: { fontSize: "10px" } } },
    yaxis: { allowDecimals: false },
    grid: { borderColor: ICCC_COLORS.divider },
    tooltip: { theme: controller?.darkMode ? "dark" : "light" },
  };

  return (
    <MDBox sx={{ p: 1.5 }}>
      <MDTypography variant="caption" sx={{ color: ICCC_COLORS.textMuted, display: "block", mb: 0.5 }}>
        Alerts raised per day — last {TREND_DAYS} days
      </MDTypography>
      <Chart options={options} series={[{ name: "Alerts", data }]} type="bar" height={220} />
    </MDBox>
  );
}

AlertTrendChart.propTypes = {
  alerts: PropTypes.arrayOf(PropTypes.object).isRequired,
};
